// apps/client/src/layouts/ClientLayout.tsx

import { useEffect, useRef } from "react";
import { Outlet } from "react-router-dom";
import Sidebar from "@/components/layout/Sidebar";
import Header from "@/components/layout/Header";
import { useUIStore } from "@/stores/uiStore";
import { useAuthStore } from "@/stores/authStore";
import { useNotificationStore } from "@/stores/notificationStore";
import { connectNotificationSocket } from "@/services/socket.service";
import { notificationsService } from "@/services/notifications.service";
import { useProfile } from "@/hooks/useProfile";

export default function ClientLayout() {
  const { sidebarCollapsed } = useUIStore();
  const token = useAuthStore((s) => s.accessToken);
  const { addNotification, setNotifications } = useNotificationStore();
  const { data: profile } = useProfile();
  const socketRef = useRef(false);

  // ─── Set browser tab title to the logged-in client's company name ────────
  useEffect(() => {
    if (profile?.companyName) {
      document.title = `YOGO Client | ${profile.companyName}`;
    }
    return () => {
      document.title = "YOGO | Supply Chain Management";
    };
  }, [profile?.companyName]);

  const mainPadding = sidebarCollapsed ? "lg:pl-[68px]" : "lg:pl-[240px]";

  // ─── Load initial notifications + unread count from REST ─────────────────
  useEffect(() => {
    if (!token) return;

    notificationsService
      .list(1, 20)
      .then((res) => {
        setNotifications(
          res.data.map((n) => ({
            id: n.id,
            title: n.title,
            message: n.message,
            type: n.type as "order" | "shipment" | "payment" | "system",
            isRead: n.isRead,
            createdAt: n.createdAt,
          })),
        );
      })
      .catch(() => {
        // silent fail — notifications not critical
      });
  }, [token, setNotifications]);

  // ─── Connect notification socket + pipe events to Zustand store ───────────
  useEffect(() => {
    if (!token || socketRef.current) return;
    socketRef.current = true;

    const socket = connectNotificationSocket(token);

    socket.on("notification:new", (payload) => {
      addNotification({
        id: payload.notification.id,
        title: payload.notification.title,
        message: payload.notification.message,
        type: (payload.notification.type ?? "system") as
          | "order"
          | "shipment"
          | "payment"
          | "system",
        isRead: false,
        createdAt: payload.notification.createdAt,
      });
    });

    // ⚠️ Do NOT disconnect on route change
    // Socket stays alive for entire session
    // Only disconnects on logout
  }, [token, addNotification]);

  // ✅ FIXED (proper scroll container pattern)
  return (
    <div className="h-screen overflow-hidden bg-slate-50">
      <Sidebar />
      <div
        className={[
          "flex flex-col h-full transition-all duration-300",
          mainPadding,
        ].join(" ")}
      >
        <Header />
        <main className="flex-1 overflow-y-auto p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

// Day 46 complete
